import { useState } from "react";
import { Droplets } from "lucide-react";
import { SPCChart } from "@/components/charts/SPCChart";
import { HelpIcon } from "@/components/HelpTooltip";
import { useSetPageHeader } from "@/components/layout/PageHeaderContext";
import {
  useRawMaterials,
  useRawMaterialSPC,
  useRawMaterialSuppliers,
  useRawMaterialTests,
} from "@/hooks/use-raw-materials";
import { formatNumber, daysAgo, today } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import type { SPCAlert } from "@shared/types";

function groupAlerts(alerts: SPCAlert[]) {
  const map = new Map<
    string,
    { rule: string; severity: "warning" | "critical"; description: string; count: number }
  >();
  for (const a of alerts) {
    const key = `${a.rule}_${a.description}`;
    const existing = map.get(key);
    if (existing) existing.count++;
    else map.set(key, { rule: a.rule, severity: a.severity, description: a.description, count: 1 });
  }
  return Array.from(map.values()).sort((a, b) => {
    if (a.severity === "critical" && b.severity !== "critical") return -1;
    if (b.severity === "critical" && a.severity !== "critical") return 1;
    return b.count - a.count;
  });
}

export function RawMaterialsQA() {
  useSetPageHeader({
    title: "Raw Materials QA",
    subtitle: "SPC analysis for incoming raw material tests",
    icon: Droplets,
  });

  const [material, setMaterial] = useState("");
  const [test, setTest] = useState("");
  const [from, setFrom] = useState(() => daysAgo(730)); // 2 years — small datasets
  const [to, setTo] = useState(() => today());
  const [supplier, setSupplier] = useState("");

  const { data: materials, isLoading: loadingMaterials } = useRawMaterials();
  const { data: testTypes } = useRawMaterialTests();
  const { data: spc, isLoading: loadingSPC } = useRawMaterialSPC(
    material || null,
    test || null,
    from,
    to,
    undefined,
    supplier || undefined
  );
  const { data: suppliers } = useRawMaterialSuppliers(material || null);

  // Get test options from server
  const tests = (testTypes || []).map((t: { key: string; label: string }) => ({
    value: t.key,
    label: t.label,
  }));

  // Auto-select first test when material changes or tests load
  const handleMaterialChange = (slug: string) => {
    setMaterial(slug);
    setSupplier("");
    if (tests.length > 0) setTest(tests[0].value);
  };

  // Auto-select first test when tests load and none selected
  if (!test && tests.length > 0) {
    setTest(tests[0].value);
  }

  const testLabel = tests.find((t) => t.value === test)?.label || test;

  if (loadingMaterials) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Filters */}
      <div className="flex flex-wrap items-end gap-3">
        {/* Material selector */}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            Material
          </label>
          <select
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={material}
            onChange={(e) => handleMaterialChange(e.target.value)}
          >
            <option value="">Select material...</option>
            {(materials || []).map((m) => (
              <option key={m.slug} value={m.slug} disabled={m.sample_count === 0}>
                {m.label} ({m.sample_count})
              </option>
            ))}
          </select>
        </div>

        {/* Test type selector */}
        {tests.length > 0 && (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Test
            </label>
            <div className="flex gap-1">
              {tests.map((t) => (
                <button
                  key={t.value}
                  onClick={() => setTest(t.value)}
                  className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                    test === t.value
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Date range */}
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            From
          </label>
          <input
            type="date"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium text-muted-foreground">
            To
          </label>
          <input
            type="date"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm"
            value={to}
            onChange={(e) => setTo(e.target.value)}
          />
        </div>

        {/* Supplier filter */}
        {suppliers && suppliers.length > 1 && (
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Supplier
            </label>
            <select
              className="h-9 rounded-md border border-input bg-background px-3 text-sm"
              value={supplier}
              onChange={(e) => setSupplier(e.target.value)}
            >
              <option value="">All suppliers</option>
              {suppliers.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* No material selected */}
      {!material && (
        <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
          Select a raw material to view SPC analysis
        </div>
      )}

      {/* Loading */}
      {material && test && loadingSPC && (
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* SPC Chart + Stats */}
      {material && test && spc && !loadingSPC && (
        <>
          {spc.unavailable ? (
            <div className="rounded-xl border border-border bg-card p-12 text-center text-muted-foreground">
              {spc.message || "Test unavailable for this material"}
            </div>
          ) : (
            <div className="space-y-4">
              {/* Stats cards */}
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs text-muted-foreground">
                    Mean <HelpIcon k="sigma" />
                  </p>
                  <p className="text-xl font-bold">
                    {formatNumber(spc.stats.mean)}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs text-muted-foreground">
                    Std Dev <HelpIcon k="stddev" />
                  </p>
                  <p className="text-xl font-bold">
                    {formatNumber(spc.stats.stddev)}
                  </p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs text-muted-foreground">
                    Samples
                  </p>
                  <p className="text-xl font-bold">{spc.stats.count}</p>
                </div>
                <div className="rounded-xl border border-border bg-card p-4">
                  <p className="text-xs text-muted-foreground">
                    Alerts <HelpIcon k="nelson_rules" />
                  </p>
                  <p className="text-xl font-bold">
                    {spc.alerts.length}
                  </p>
                </div>
              </div>

              {/* Chart */}
              <div className="rounded-xl border border-border bg-card p-4">
                <SPCChart
                  data={spc.data}
                  stats={spc.stats}
                  spec={spc.spec}
                  alerts={spc.alerts}
                  testLabel={testLabel}
                />
              </div>

              {/* Nelson Rules summary */}
              {spc.alerts.length > 0 && (
                <div className="rounded-xl border border-border bg-card p-4">
                  <h3 className="mb-3 text-sm font-semibold">
                    Nelson Rules Alerts
                  </h3>
                  <div className="space-y-2">
                    {groupAlerts(spc.alerts).map((g) => (
                      <div
                        key={g.rule + g.description}
                        className="flex items-center gap-3 text-sm"
                      >
                        <span
                          className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                            g.severity === "critical"
                              ? "bg-red-500/10 text-red-500"
                              : "bg-amber-500/10 text-amber-500"
                          }`}
                        >
                          {g.rule}
                        </span>
                        <span className="text-muted-foreground">
                          {g.description}
                        </span>
                        <span className="ml-auto tabular-nums text-muted-foreground">
                          {g.count}x
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
